/**
 * AppSwitcherDialog component
 * Full-screen dialog for switching between apps in the current workspace.
 * Shows all installed apps with agent/chat counts.
 */

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { fetchApps, type AppRecord } from '@/services/app-service'
import { setDefaultAppId } from '@/hooks/use-default-app'
import { Search, Store } from 'lucide-react'
import { cn } from '@/lib/utils'

interface AppSwitcherDialogProps {
  /** Whether the dialog is open */
  open: boolean
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void
  /** Currently active app ID */
  activeAppId?: string
}

/**
 * AppSwitcherDialog — shows all apps in workspace, allows switching
 */
export function AppSwitcherDialog({
  open,
  onOpenChange,
  activeAppId,
}: AppSwitcherDialogProps) {
  const navigate = useNavigate()
  const { appId } = useParams<{ appId?: string }>()
  const [apps, setApps] = useState<AppRecord[]>([])
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!open) return

    setIsLoading(true)
    fetchApps()
      .then(setApps)
      .catch(() => setApps([]))
      .finally(() => setIsLoading(false))
  }, [open])

  const filteredApps = search
    ? apps.filter(
        (app) =>
          app.name.toLowerCase().includes(search.toLowerCase()) ||
          app.category?.toLowerCase().includes(search.toLowerCase())
      )
    : apps

  const handleSelectApp = (app: AppRecord) => {
    setDefaultAppId(app.id)
    onOpenChange(false)
    navigate(`/app/${app.id}/chat`)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md gap-0 p-0">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-base font-semibold">Switch App</DialogTitle>
        </DialogHeader>

        {/* Search */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Search apps..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
              autoFocus
            />
          </div>
        </div>

        {/* Apps list */}
        <ScrollArea className="max-h-[320px]">
          <div className="px-2 pb-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
              </div>
            ) : filteredApps.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                {search ? 'No apps match your search' : 'No apps installed'}
              </div>
            ) : (
              filteredApps.map((app) => {
                const isActive = app.id === activeAppId
                const isIconUrl =
                  app.icon && (app.icon.startsWith('http') || app.icon.startsWith('/'))

                return (
                  <button
                    key={app.id}
                    onClick={() => handleSelectApp(app)}
                    className={cn(
                      'flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-left transition-colors',
                      isActive
                        ? 'bg-primary/5 border border-primary/20'
                        : 'hover:bg-muted border border-transparent'
                    )}
                  >
                    <div
                      className={cn(
                        'size-10 rounded-lg flex items-center justify-center shrink-0 text-base overflow-hidden',
                        isActive ? 'bg-primary/10' : 'bg-muted'
                      )}
                    >
                      {isIconUrl ? (
                        <img src={app.icon!} alt="" className="size-10 object-cover" />
                      ) : (
                        app.icon || '📱'
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p
                          className={cn(
                            'text-sm font-medium truncate',
                            isActive ? 'text-primary' : 'text-foreground'
                          )}
                        >
                          {app.name}
                        </p>
                        {isActive && (
                          <span className="size-2 rounded-full bg-primary shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {app.category || 'Application'}
                      </p>
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </ScrollArea>

        {/* Marketplace link */}
        <div className="border-t px-4 py-3">
          <button
            onClick={() => {
              onOpenChange(false)
              navigate(`/app/${appId || activeAppId}/marketplace`)
            }}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Store className="size-4" />
            Browse Marketplace
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
